<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router/composables';
import { useArticleDetails } from '@/common/api/article.api';
import GenericHeader from '@/components/GenericHeader.vue';
import LoadingState from '@/components/loading/LoadingState.vue';
import ErrorState from '@/components/loading/ErrorState.vue';
import RelatedArticles from './components/RelatedArticles.vue';
import ArticleRating from './components/ArticleRating.vue';
import ArticleAction from './components/ArticleAction.vue';
import ArticleAvailableActions from './components/ArticleAvailableActions.vue';
import {
  useArticleHeaderCustomisation,
  useArticleHeaderSearchCustomisation,
  useArticleHeaderTitleCustomisation,
  useArticleNavIcon1Customisation,
  useArticleNavIcon2Customisation,
} from './composables/useArticleHeaderCustomisation';
import { useArticleContentCustomisation } from './composables/useArticleContentCustomisation';
import { useArticleMetadata } from './composables/useArticleMetadata';
import { useArticleMetrics } from './composables/useArticleMetrics';
import { useHeaderProps, usePageBodyStyle } from '@/common/customisation/composables';
import { createTextStyle } from '@/common/customisation/utils/styleUtils';
import { getErrorMessage } from '@/common/utils/error.utils';
import { useArticleAction } from './composables/useArticleAction';
import { useChatWidget } from '@/stores/useChatWidget';
import { ChatTriggerMatchSource } from '@/common/bot/flow-frameworks/chat-trigger';
import { useDialogTrigger } from '@/stores/useDialogTrigger';
import { OpenRequestModal } from '@/components/OpenRequestModal';
import PlsDialog from '@/plugins/Dialog.vue';
import { useWidgetConversation } from '@/stores/useWidgetConversation';
import { ExternalBotEvent } from '@/common/bot/enum';
import { randomId } from '@/common/bot/lib';
import type { ArticleAction as ArticleActionType } from '@/common/bot/types';
import { useMockedFlowStore } from '@/library/cf-v1/mocked-flow';
import { PUI } from '@pleased-npm/fe-customisation';
import { analytics, syncLogs, CMP_CONTEXTS } from '@/common/logs';
import { BTN_EVTS } from '@/directives/trackClick';
import { platform } from '@/common/bridge';

const route = useRoute();
const router = useRouter();

const articleIdParam = computed(() => route.params?.id as string);
const articleId = computed(() => Number(articleIdParam.value));

// Widget + Chatroom should be quite same
const chatWidget = useChatWidget();
const { setIsChatOverlayVisible } = chatWidget;

const widgetConversation = useWidgetConversation();
const { submitFlowExternalAction } = widgetConversation;

const mockedFlowStore = useMockedFlowStore();
const { isActive: isMockedFlowActive } = storeToRefs(mockedFlowStore);

// Header customisation
const headerCustomisation = useArticleHeaderCustomisation();
const titleCustomisation = useArticleHeaderTitleCustomisation();
const contentCustomisation = useArticleContentCustomisation();
const navIcon1Customisation = useArticleNavIcon1Customisation();
const navIcon2Customisation = useArticleNavIcon2Customisation();
const searchCustomisation = useArticleHeaderSearchCustomisation();

const { headerProps } = useHeaderProps({
  header: headerCustomisation,
  title: titleCustomisation,
  navIcon1: navIcon1Customisation,
  navIcon2: navIcon2Customisation,
  search: searchCustomisation,
});

// Content styles
const contentTitleStyle = computed(() => createTextStyle(contentCustomisation.labelColor));
const contentBodyStyle = computed(() => createTextStyle(contentCustomisation.textColor));

const { contentStyle } = usePageBodyStyle({
  registryId: PUI.REGISTRY_IDS.ARTICLE,
  sectionKey: PUI.ARTICLES_SECTION_KEYS.BODY_BACKGROUND,
  blockKey: PUI.ARTICLES_BLOCK_KEYS.BODY_SECTION,
});

// Article data
// TanStack Query hook for fetching article details
// Uses the same query key as SelfServiceLink.vue so they share the cache
const { data: articleData, isLoading: isLoadingArticle, error: queryError } = useArticleDetails(articleIdParam);
const articleTitle = computed(() => articleData.value?.title || '');
const articleBody = computed(() => articleData.value?.body || '');
const errorMessage = computed(() => getErrorMessage(queryError.value, 'Failed to load article'));

// Article metadata and actions
const {
  canConnectToSupport,
  articleMetadata,
  enableMetadataRules,
  articleConfig,
  emitArticleInteraction,
  isAiFlowEnabled,
} = useArticleMetadata({ articleId: articleIdParam });

const { canShowAction, goToArticleAction } = useArticleAction({ articleId: articleIdParam });

// Dialog Trigger Store
const dialogTriggerStore = useDialogTrigger();
const { triggerDialog, currentTrigger, showTriggerDialog, showOpenRequestDialog } = storeToRefs(dialogTriggerStore);
const { setupTriggerDialog, onTriggerMatch, checkTrigger } = dialogTriggerStore;

// Setup trigger match handler
onTriggerMatch((trigger) => {
  setupTriggerDialog(trigger, () => {
    router.push('/new-home');
  });
});

// Selected options for OpenRequestModal
const selectedOptions = computed(() => {
  return {
    category: undefined,
    section: undefined,
    article: articleTitle.value,
  };
});

// Article metrics tracking
const abValue = computed(() => articleConfig.value?.abValue || '');
useArticleMetrics({
  articleId: articleIdParam,
  canConnectToSupport,
  abValue,
});

// Route query params
//TODO: handle back, when article is opened from chat widget
const isFromChatWidget = computed(() => router.currentRoute.query['chatWidget'] === 'true');
const sourceMessageId = computed(() => router.currentRoute.query['sourceMessageId'] as string);

// Computed article action for available actions component
const availableArticleAction = computed<ArticleActionType | null>(() => {
  if (!canShowAction.value.box || canShowAction.value.warning) {
    return null;
  }
  return articleMetadata.value.articleAction || null;
});

const handleBackClick = () => {
  analytics.trackClick({
    name: BTN_EVTS.ARTICLE_INTERACT_BACK,
    selection: articleId.value,
  });

  if (!isFromChatWidget.value) {
    router.back();
    return true;
  }

  emitArticleInteraction('back', sourceMessageId.value);
  setIsChatOverlayVisible(true);
  router.back();

  return true;
};

const handleConnectSupport = () => {
  analytics.trackClick({
    name: BTN_EVTS.ARTICLE_INTERACT_CONTACT_SUPPORT,
    selection: articleId.value,
  });

  emitArticleInteraction('support-request', sourceMessageId.value);
  setIsChatOverlayVisible(true);
};

const handleArticleInteractAction = async () => {
  analytics.trackClick({
    name: BTN_EVTS.ARTICLE_INTERACT_ACTION,
    selection: articleId.value,
  });

  const isAppPlatform = platform === 'ios' || platform === 'app';
  await syncLogs({ endSession: !isAppPlatform });
  goToArticleAction();
};

// OpenRequestModal handlers
function handleEndChat() {
  // TODO: proper end chat
  submitFlowExternalAction({ type: ExternalBotEvent.RESTART_CHAT, id: randomId() });
  router.push('/new-home');
}

function handleRestartChat() {
  submitFlowExternalAction({ type: ExternalBotEvent.RESTART_CHAT, id: randomId() });
  router.push('/new-home');
}

function handleGoToMyRequest() {
  router.push('/conversations');
}

onMounted(() => {
  if (isMockedFlowActive.value) return;
  setIsChatOverlayVisible(false);
  checkTrigger({ type: ChatTriggerMatchSource.ARTICLE, id: articleIdParam.value });
});
</script>

<template>
  <div class="page-container">
    <GenericHeader
      :header="headerProps.header"
      :title="headerProps.title"
      :search="headerProps.search"
      :nav-icon1="headerProps.navIcon1"
      :nav-icon2="headerProps.navIcon2"
      :track-event-context="CMP_CONTEXTS.ARTICLE"
      @back="handleBackClick"
    />

    <!-- Content Container - Scrollable -->
    <div class="content-container page-content" :style="contentStyle">
      <LoadingState v-if="isLoadingArticle" />

      <ErrorState v-else-if="queryError" :message="errorMessage" />

      <template v-else>
        <div class="article-title theme-font-h2" :style="contentTitleStyle">
          {{ articleTitle }}
        </div>
        <div class="article-body-content theme-font-main" :style="contentBodyStyle" v-html="articleBody" />

        <ArticleRating :article-id="articleId" />
        <ArticleAction :article-id="articleId" :can-connect-to-support="canConnectToSupport" />
      </template>

      <RelatedArticles :article-id="articleId" />

      <ArticleAvailableActions
        :article-action="availableArticleAction"
        :can-connect-to-support="articleMetadata.canConnectToSupport || false"
        :enable-metadata-rules="enableMetadataRules || false"
        :is-ai-flow-enabled="isAiFlowEnabled"
        @back="handleBackClick"
        @connect="handleConnectSupport"
        @action="handleArticleInteractAction"
      />
    </div>

    <!-- Trigger Dialog -->
    <PlsDialog v-if="showTriggerDialog" v-bind="triggerDialog" />

    <!-- Open Request Modal -->
    <OpenRequestModal
      v-if="showOpenRequestDialog && currentTrigger"
      :trigger="currentTrigger"
      :selected-options="selectedOptions"
      @end-chat="handleEndChat"
      @restart-chat="handleRestartChat"
      @go-to-my-request="handleGoToMyRequest"
    />
  </div>
</template>

<style scoped lang="scss">
.content-container {
  padding: 16px 20px;
  padding-bottom: 100px;

  .article-title {
    padding: 16px 0;
  }

  .article-body-content {
    overflow-x: hidden;
    max-width: 100%;

    :deep(img) {
      max-width: 100%;
      height: auto;
      object-fit: contain;
      display: block;
    }
  }
}
</style>
